import { PlanogramItem, PlanogramItemNonCircular, UnrangedPlanogramItem } from "./item";
import { PlanogramBay } from "./bay";
import {
  getEffectiveMerchandisableWidth,
  getMergedDeadSpans,
  getMergedMerchandisableWidth,
  getLiveSpans,
  getShelfItemLayout,
  getShelfItemWidth,
  isMergeableOnSide,
  itemsFitAroundDeadSpans,
  type ShelfDeadSpan,
} from "@CommonUtils/pog/shelfMerchandising";
import { getShelfItemDividerSlots } from "@CommonUtils/pog/shelfDividers";
import { rackNameOf } from "@CommonUtils/pog/racks";
import type { DividersSetByHand } from "@CommonTypes/backend/PspRules";
import { usableShelfDepth } from "@CommonUtils/pog/shelfLoader";

interface PlanogramShelfCommon {
  uniqueId: string;
  width: number;
  merchandisableWidth?: number | null;
  /**
   * Strips of this shelf blocked by an obstruction (a pole, a duct), in
   * shelf-relative x. `merchandisableWidth` is the width minus these. Empty
   * for a clear shelf; pogs before 3.10 lack it, and their shorter
   * merchandisable width still means a dead strip at the right end.
   */
  deadSpans?: ShelfDeadSpan[];
  depth: number;
  maxFrontOverhang?: number;
  slope?: number;
  thickness: number;
  offset: number;
  grillHeight: number;
  type: "REGULAR" | "RISER" | "HANGCELL";
  variantRestrictions: PlanogramShelfVariantRestriction[];
  align?: "left" | "right" | "between" | "around";
  /**
   * The maximum load in pounds this board may carry, or null when no source
   * has supplied one — which today is ALWAYS. Do not read the PSA's
   * `Fixture.weightCapacity` into this field: measured across real files it
   * is a dead placeholder (constant 1 or 0), not a fixture spec — see
   * owa-frontend docs/shelf-weight/AGENTS.md §5. The field exists so a future
   * real source (fixture master data, merchant spec sheet) has a home.
   * Optional in data contracts (pogs older than 3.7 lack it), normalized to
   * null in the class.
   */
  weightCapacity?: number | null;
  /**
   * The cap this shelf was judged against by the last pogger run, with its
   * provenance — echoed from `shelf_loads[].limit_g` / `limit_source`.
   *
   * THE ONLY SOURCE OF A SHELF LIMIT: null means no cap was supplied, which
   * renders as NO VERDICT, never as compliant or a guessed default — pogger
   * holds the same line (`limit_source: null`, `resolution_status: "no_cap"`).
   *
   * Distinct from `weightCapacity` (a fixture spec, no data source today).
   * `"request_default"` is the run's own `default_shelf_weight_limit`; a real
   * per-shelf rating comes back as `"shelf"`. Pounds; null for pogs before 3.9.
   */
  effectiveWeightLimit?: EffectiveWeightLimit | null;
  /**
   * Set on a pallet's depth lane only: 1 is the front lane. The lanes of a
   * pallet all stand on its deck, so their notch numbers order them (front
   * lane first) and say nothing about their height.
   */
  laneNo?: number;
  /**
   * The PSA Fixture "Assembly": the name of the group of boards this one is
   * moved with, e.g. "WIPES RACK" for the tiers of a wipes rack. Set on input
   * (PSA import, the fixture library, a datafix) and kept through every save.
   * Optional in data contracts, normalized to null in the class.
   */
  assembly?: string | null;
  /**
   * A rack board's host: the `uniqueId` of the ordinary shelf it stands on, derived once on
   * input (`deriveStandsOn`) and stored, never guessed later. Null on any other board.
   */
  standsOn?: string | null;
  /**
   * The divider hardware on this shelf, or null when it has none. Lengths in
   * the pog's x100 units, like `width`. Set from the fixture on PSA import and
   * from the pogger response on generation; PSA export writes it back to the
   * fixture record. Optional in data contracts (pogs older than 4.3 lack it),
   * normalized to null in the class. Item `dividerWidth` is a product fact and
   * is not this field.
   */
  dividers?: PlanogramShelfDividers | null;
  /**
   * The store fixture row's hand override when this pog was generated (null:
   * follow the flow's divider policy). A copy for display; the row is the source.
   */
  dividersSetByHand?: DividersSetByHand | null;
  /**
   * True when this shelf's dividers were kept from its store fixture row: the
   * flow's `keepFixtureDividers` switch is on, the row has valid dividers and
   * no hand override, so the request sent them like a hand "always" rather
   * than `follow_products`. Null otherwise (and on pogs generated before it).
   * Such a shelf is exempt from "Dividers on a shelf where no product needs
   * them", like a hand "always". `dividersSetByHand` stays the row's own
   * override, which the shelf panel's control reads, so this is not folded in.
   */
  dividersKeptFromFixture?: boolean | null;
  /**
   * Depth a spring loader (pusher) takes at the back of this shelf, in the
   * pog's x100 units like `depth`; null or 0 for none. `depth` stays physical:
   * the stock uses `usableShelfDepth` (depth minus this) and sits at the front.
   * Set from the fixture on PSA import (a negative back overhang) and from the
   * store's fixture row on generation; on a solved store shelf it is the
   * loader the pogger left in (`loader.active`), null when it left it out.
   * PSA export writes it back. Optional in data contracts (pogs older than 4.5
   * lack it), normalized to null in the class.
   */
  loaderDepth?: number | null;
  /** Null when neither a loader rule nor a Fixtures page value judged the shelf. */
  loaderDecision?: ShelfLoaderDecision | null;
}

/**
 * Why the shelf has a loader or not. Decided before the solve on the base shelf
 * at the same position; a "follow_products" shelf's reason is then replaced by
 * the pogger's decision (see `ShelfLoaderDecision`). "depth-not-set": a loader
 * was due but no depth was known (no flow depth, and no loader anywhere in the
 * base planogram), so none was sent. The shelf panel's Why
 * (`shelfLoaderWhy`) reads the solved shelf's own products.
 */
export type ShelfLoaderDecisionReason =
  | "set-on-fixtures-page"
  | "set-none-on-fixtures-page"
  | "needed"
  | "depth-not-set"
  | "needs-more-than-half"
  | "not-needed"
  /** Loader mode "shelf": the base shelf has a loader, so the store shelf keeps it. */
  | "on-base-shelf"
  /** Loader mode "shelf": the base shelf has none, so the store shelf has none. */
  | "not-on-base-shelf";

/** The request's shelf `loader.mode`, as the pogger echoes it. */
export type ShelfLoaderBlockMode = "fixed" | "follow_products";

/**
 * Before the solve: why the request offered the shelf a loader or not. After a
 * solve whose shelf had a `loader` block, the pogger's decision is folded in:
 * `mode`, `active` and `needFacings` are set, and on a "follow_products" shelf
 * `reason` and `depth` state the solved outcome ("needed" with the depth when
 * the loader went in, else "needs-more-than-half" or "not-needed" with null).
 */
export interface ShelfLoaderDecision {
  reason: ShelfLoaderDecisionReason;
  /** The loader's depth when the shelf has one (x100), else null. */
  depth: number | null;
  /** Counted on the base shelf's facings, before the solve. */
  needing: number;
  facings: number;
  /** The `loader` block the request sent; null or absent when it sent none. */
  mode?: ShelfLoaderBlockMode | null;
  /** The pogger's `loader.active`: whether the loader is in. Null or absent without a block. */
  active?: boolean | null;
  /** The pogger's `loader.needs_loader_facings`: facings on the solved shelf that need a loader. */
  needFacings?: number | null;
  /**
   * Where the offered loader's depth came from when it was not the flow's, the
   * Fixtures page's or the base shelf's own: "base-typical" is the base
   * planogram's most common loader depth, offered ("follow_products") on a shelf
   * whose base shelf has none. Absent otherwise, and dropped when the pogger left
   * the loader out.
   */
  depthSource?: ShelfLoaderDepthSource | null;
}

/** See `ShelfLoaderDecision.depthSource`. */
export type ShelfLoaderDepthSource = "base-typical";

/**
 * Shelf divider hardware, mirroring the pogger's `Dividers` and the PSA
 * fixture's divider fields.
 */
export interface PlanogramShelfDividers {
  width: number;
  height: number;
  depth: number;
  atStart: boolean;
  atEnd: boolean;
  /**
   * `"dividers_between_facings"`: one divider after every facing column (PSA
   * "Dividers between facings"). A number: a fixed count; 0 means no dividers
   * between facings (the start/end flags may still place some).
   */
  numberOfDividers: number | "dividers_between_facings";
  /**
   * The pogger's decision on a shelf it was asked to fit to its products
   * (`follow_products`). Null or absent: the shelf states its dividers outright.
   * False: the hardware is recorded but not applied.
   */
  active?: boolean | null;
}

/** A shelf cap the optimizer actually applied, with its provenance. */
export interface EffectiveWeightLimit {
  pounds: number;
  source: "shelf" | "request_default";
}

export interface PlanogramShelfNonCircular extends PlanogramShelfCommon {
  notchNo: number;
  mergedLeft: string | null;
  mergedRight: string | null;
  items: PlanogramItemNonCircular[];
  bay: string;
}

export interface PlanogramShelfVariantRestriction {
  value: string;
  type: "MAY_HAVE" | "MUST_HAVE" | "MUST_NOT_HAVE";
  /** The rule as written; its shelfRule numbers the base wall's shelves, not necessarily this pog's. */
  sourceRule?: {
    groupId: string;
    bayRule: string;
    shelfRule: string;
    condition: string;
  };
}

export class PlanogramShelf implements PlanogramShelfCommon {
  uniqueId: string;
  width: number;
  merchandisableWidth?: number | null;
  deadSpans: ShelfDeadSpan[];
  depth: number;
  maxFrontOverhang?: number;
  slope?: number;
  thickness: number;
  offset: number;
  grillHeight: number;
  type: "REGULAR" | "RISER" | "HANGCELL";
  variantRestrictions: PlanogramShelfVariantRestriction[];
  mergedLeft: PlanogramShelf | null;
  mergedRight: PlanogramShelf | null;
  items: PlanogramItem[];
  bay: PlanogramBay;
  align?: "left" | "right" | "between" | "around";
  /** See PlanogramShelfCommon — always null today; no data source carries a real limit. */
  weightCapacity: number | null;
  /** See PlanogramShelfCommon — the served cap, or null for no verdict. */
  effectiveWeightLimit: EffectiveWeightLimit | null;
  laneNo?: number;
  /** See PlanogramShelfCommon — the board's assembly name, or null. */
  assembly: string | null;
  /** See PlanogramShelfCommon — the host shelf's uniqueId, or null. */
  standsOn: string | null;
  /** See PlanogramShelfCommon — divider hardware, or null for none. */
  dividers: PlanogramShelfDividers | null;
  dividersSetByHand: DividersSetByHand | null;
  /** See PlanogramShelfCommon: true when the keep switch kept the fixture row's dividers. */
  dividersKeptFromFixture: boolean | null;
  /** See PlanogramShelfCommon — spring loader depth, or null for none. */
  loaderDepth: number | null;
  loaderDecision: ShelfLoaderDecision | null;

  private notchNo: number;

  constructor(shelf: Omit<PlanogramShelfNonCircular, "bay">, bay: PlanogramBay) {
    this.uniqueId = shelf.uniqueId;
    this.width = shelf.width;
    this.merchandisableWidth = shelf.merchandisableWidth;
    this.deadSpans = (shelf.deadSpans ?? []).map((span) => ({ ...span }));
    this.depth = shelf.depth;
    this.maxFrontOverhang = shelf.maxFrontOverhang ?? 0;
    this.slope = shelf.slope ?? 0;
    this.thickness = shelf.thickness;
    this.offset = shelf.offset;
    this.notchNo = shelf.notchNo;
    this.grillHeight = shelf.grillHeight;
    this.type = shelf.type;
    this.variantRestrictions = [...shelf.variantRestrictions];
    this.mergedLeft = null;
    this.mergedRight = null;
    this.items = shelf.items.map((item) => new PlanogramItem(item, this));
    this.bay = bay;
    this.align = shelf.align || "between";
    this.weightCapacity = shelf.weightCapacity ?? null;
    this.effectiveWeightLimit = shelf.effectiveWeightLimit ?? null;
    if (shelf.laneNo !== undefined) {
      this.laneNo = shelf.laneNo;
    }
    this.assembly = shelf.assembly?.trim() || null;
    this.standsOn = shelf.standsOn || null;
    this.dividers = shelf.dividers ? { ...shelf.dividers } : null;
    this.dividersSetByHand = shelf.dividersSetByHand ?? null;
    this.dividersKeptFromFixture = shelf.dividersKeptFromFixture ?? null;
    this.loaderDepth = shelf.loaderDepth ?? null;
    this.loaderDecision = shelf.loaderDecision ? { ...shelf.loaderDecision } : null;
  }

  /** Depth the stock may use: physical depth minus the spring loader. */
  getUsableDepth = () => usableShelfDepth(this);

  getNo = () => {
    return this.bay.shelves.findIndex((shelf) => shelf.uniqueId === this.uniqueId) + 1;
  };

  getName = () => {
    return `${this.bay.getNo()}.${this.getNo()}`;
  };

  getNotchNo = () => {
    return this.notchNo;
  };

  getNewNotchNo = (newY: number) => {
    return Math.max(
      1,
      Math.min(
        this.bay.maxNotch,
        Math.round(
          (newY - this.bay.notchOffset - this.offset + this.thickness) / this.bay.notchSpacing + 1,
        ),
      ),
    );
  };

  getYBottom = () => {
    // Every lane of a pallet stands on the deck, whatever its notch number.
    if (this.bay.pallet) {
      return this.bay.notchOffset - this.thickness;
    }

    return (
      this.bay.notchOffset +
      (this.notchNo - 1) * this.bay.notchSpacing +
      this.offset -
      this.thickness
    );
  };

  /**
   * Where the room above this board ends: under the lowest board above that
   * stands over this board's live part, or the bay's top (see `getCeilingAt`).
   * A board beside it at the same notch, or a rack's tier over the host's dead
   * span, is not over it.
   */
  getYTop = () => {
    // A lane is stacked to the top of the pallet: the lane in front of it is
    // beside it in depth, not above it.
    if (this.bay.pallet) {
      return this.bay.height;
    }

    return this.getOwnCeiling();
  };

  /**
   * How far the shelf's back edge stands from the back of its bay. A gondola
   * shelf is at the back; a pallet's lane stands in front of the lanes behind
   * it, the front lane flush with the pallet's front.
   */
  getZBack = () => {
    if (!this.bay.pallet || this.laneNo === undefined) {
      return 0;
    }

    return this.bay.depth - this.laneNo * this.bay.pallet.laneDepth;
  };

  /** This board's live parts in pog x, left to right (see `getLiveSpans`). */
  getLiveXSpans = (): { xLeft: number; xRight: number }[] => {
    const xLeft = this.getXLeft();
    return getLiveSpans(this).map((span) => ({
      xLeft: xLeft + span.x,
      xRight: xLeft + span.x + span.width,
    }));
  };

  /** The live parts, or the whole board when nothing on it is live. */
  private getOwnSpans = (): [number, number][] => {
    const spans = this.getLiveXSpans().map(({ xLeft, xRight }): [number, number] => [xLeft, xRight]);
    return spans.length > 0
      ? spans
      : [[this.getXLeft(), this.width > 0 ? this.getXRight() : this.bay.getXRight()]];
  };

  /**
   * The bays whose boards can stand over this one between pog x `xLeft` and
   * `xRight`: every bay the range crosses (a merged run crosses bays). A
   * pallet keeps to its own bay, as its lanes stand on its deck.
   */
  private getBaysAcross = (xLeft: number, xRight: number) => {
    if (this.bay.pallet) return [this.bay];
    const bayLeft = this.bay.getXLeft();
    if (xLeft >= bayLeft && xRight <= bayLeft + this.bay.width) return [this.bay];
    return this.bay.planogram.bays.filter(
      (bay) =>
        bay === this.bay || (!bay.pallet && bay.getXLeft() < xRight && bay.getXRight() > xLeft),
    );
  };

  /**
   * Where this board is a ceiling for the boards below, in pog x: its whole width (its bay with
   * no width), obstructions included; a rack's tray only over its live part, so the host beside
   * the rack is not capped by it.
   */
  private getPhysicalXSpans = (): { xLeft: number; xRight: number }[] => {
    if (rackNameOf(this.assembly) !== null) return this.getLiveXSpans();
    const xLeft = this.getXLeft();
    return [{ xLeft, xRight: xLeft + (this.width > 0 ? this.width : this.bay.width) }];
  };

  /**
   * Where the room above this board ends between pog x `xLeft` and `xRight`,
   * piece by piece, left to right. At each x it ends under the lowest board
   * strictly above this one that stands over x (see `getPhysicalXSpans`), in
   * whichever bay x falls in, or at that bay's top. Along a merged run the
   * range runs into the next bays, and so do the pieces.
   */
  private getCeilingPieces = (xLeft: number, xRight: number) => {
    const pieces: { xLeft: number; xRight: number; ceiling: number }[] = [];
    if (!(xRight > xLeft)) return pieces;
    const yBottom = this.getYBottom();
    const bays = this.getBaysAcross(xLeft, xRight);
    const above = bays.flatMap((bay) =>
      bay.shelves
        .filter((shelf) => shelf !== this && shelf.getYBottom() > yBottom)
        .map((shelf) => ({ yBottom: shelf.getYBottom(), spans: shelf.getPhysicalXSpans() })),
    );

    const cuts = new Set<number>([xLeft, xRight]);
    const cut = (x: number) => {
      if (x > xLeft && x < xRight) cuts.add(x);
    };
    for (const bay of bays) {
      cut(bay.getXLeft());
      cut(bay.getXRight());
    }
    for (const board of above) {
      for (const span of board.spans) {
        cut(span.xLeft);
        cut(span.xRight);
      }
    }
    const xs = [...cuts].sort((left, right) => left - right);

    for (let index = 0; index + 1 < xs.length; index += 1) {
      const from = xs[index];
      const to = xs[index + 1];
      const middle = (from + to) / 2;
      const bayAt =
        bays.find((bay) => bay.getXLeft() <= middle && bay.getXRight() > middle) ?? this.bay;
      let ceiling = bayAt.height;
      for (const board of above) {
        if (
          board.yBottom < ceiling &&
          board.spans.some((span) => span.xLeft <= middle && span.xRight > middle)
        ) {
          ceiling = board.yBottom;
        }
      }
      const last = pieces[pieces.length - 1];
      if (last && last.ceiling === ceiling && last.xRight === from) {
        last.xRight = to;
      } else {
        pieces.push({ xLeft: from, xRight: to, ceiling });
      }
    }
    return pieces;
  };

  /** The lowest ceiling over pog x `xLeft`-`xRight` (see `getCeilingPieces`). */
  getCeilingAt = (xLeft: number, xRight: number): number => {
    const pieces = this.getCeilingPieces(xLeft, xRight);
    return pieces.length > 0
      ? Math.min(...pieces.map((piece) => piece.ceiling))
      : this.bay.height;
  };

  /**
   * The room above this board between pog x `xLeft` and `xRight`, piece by
   * piece (see `getCeilingPieces`), never negative.
   */
  getClearanceProfile = (
    xLeft: number,
    xRight: number,
  ): { xLeft: number; xRight: number; clearance: number }[] => {
    const surface = this.getYBottom() + this.thickness;
    return this.getCeilingPieces(xLeft, xRight).map((piece) => ({
      xLeft: piece.xLeft,
      xRight: piece.xRight,
      clearance: Math.max(0, piece.ceiling - surface),
    }));
  };

  /**
   * The room over pog x `xLeft`-`xRight` above this board: the least of its
   * clearance profile there. For a stack, pass the stack's x; on a merged run
   * that x may lie in a neighbouring bay.
   */
  getClearanceAt = (xLeft: number, xRight: number): number => {
    if (!(xRight > xLeft)) return this.getClearanceAbove();
    return Math.max(0, this.getCeilingAt(xLeft, xRight) - (this.getYBottom() + this.thickness));
  };

  /**
   * The room above this board's live part, up to the lowest board above that
   * stands over it, never negative. A sibling segment at the same notch is not
   * above; a rack's tier over the host's dead span is not over the host.
   */
  getClearanceAbove = () =>
    Math.max(0, this.getOwnCeiling() - (this.getYBottom() + this.thickness));

  private getOwnCeiling = () =>
    Math.min(...this.getOwnSpans().map(([xLeft, xRight]) => this.getCeilingAt(xLeft, xRight)));

  getXLeft = () => {
    return this.bay.getXLeft();
  };

  getXRight = () => {
    return this.bay.getXLeft() + this.width;
  };

  getCoordinates = () => {
    return {
      xLeft: this.getXLeft(),
      xRight: this.getXRight(),
      yBottom: this.getYBottom(),
      yTop: this.getYTop(),
    };
  };

  getHeight = () => {
    // Hangcell fixture height is from bottom to bottom shelf.
    if (this.type === "HANGCELL") {
      let bottomShelf: PlanogramShelf | undefined = undefined;

      // Find closest bottom shelf.
      for (const shelfToCheck of this.bay.shelves) {
        if (shelfToCheck.notchNo < this.notchNo) {
          if (bottomShelf === undefined || shelfToCheck.notchNo > bottomShelf.notchNo) {
            bottomShelf = shelfToCheck;
          }
        }
      }

      if (bottomShelf) {
        return this.getYBottom() - bottomShelf.getYBottom();
      }

      return this.getYBottom();
    }

    return this.getYTop() - this.getYBottom();
  };

  /**
   * @returns Array of shelves that are merged with this shelf. Shelves in the array are from left to right.
   */
  getMergedShelves = () => {
    const mergedShelves: PlanogramShelf[] = [];
    const mostLeftMergedShelf = this.getFirstShelf();

    // Loop to most right merged shelf.
    let shelfToGet: PlanogramShelf | null = mostLeftMergedShelf;
    while (shelfToGet !== null) {
      mergedShelves.push(shelfToGet);
      shelfToGet = shelfToGet.mergedRight;
    }

    return mergedShelves;
  };

  getFirstShelf() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let firstShelf: PlanogramShelf = this;
    while (firstShelf.mergedLeft) {
      firstShelf = firstShelf.mergedLeft;
    }
    return firstShelf;
  }

  getTopShelf() {
    let topShelf = null;

    for (const shelf of this.bay.shelves) {
      if (shelf.uniqueId === this.uniqueId) {
        break;
      }

      topShelf = shelf;
    }

    return topShelf;
  }

  getBottomShelf() {
    let currentShelf = null;

    for (const shelf of this.bay.shelves) {
      if (currentShelf !== null) {
        return shelf;
      }

      if (shelf.uniqueId === this.uniqueId) {
        currentShelf = this;
      }
    }

    return null;
  }

  canBeMergedLeft = () => {
    if (this.mergedLeft || this.maxFrontOverhang) return null;
    if (!isMergeableOnSide(this, "left")) return null;

    for (const bay of this.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        if (shelf.uniqueId === this.uniqueId) continue;

        if (
          shelf.getXRight() === this.getXLeft() &&
          shelf.getYBottom() === this.getYBottom() &&
          shelf.thickness === this.thickness &&
          !shelf.maxFrontOverhang &&
          isMergeableOnSide(shelf, "right") &&
          shelf.bay.getSplitGroup() === this.bay.getSplitGroup()
        ) {
          return shelf;
        }
      }
    }

    return null;
  };

  mergeLeft = () => {
    const leftShelf = this.canBeMergedLeft();
    if (leftShelf) {
      leftShelf.mergedRight = this;
      this.mergedLeft = leftShelf;
    }
  };

  unmergeLeft = () => {
    if (this.mergedLeft) {
      this.mergedLeft.mergedRight = null;
      this.mergedLeft = null;
    }
  };

  canBeMergedRight = () => {
    if (this.mergedRight || this.maxFrontOverhang) return null;
    if (!isMergeableOnSide(this, "right")) return null;

    for (const bay of this.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        if (shelf.uniqueId === this.uniqueId) continue;

        if (
          shelf.getXLeft() === this.getXRight() &&
          shelf.getYBottom() === this.getYBottom() &&
          shelf.thickness === this.thickness &&
          !shelf.maxFrontOverhang &&
          isMergeableOnSide(shelf, "left") &&
          shelf.bay.getSplitGroup() === this.bay.getSplitGroup()
        ) {
          return shelf;
        }
      }
    }

    return null;
  };

  mergeRight = () => {
    const rightShelf = this.canBeMergedRight();
    if (rightShelf) {
      rightShelf.mergedLeft = this;
      this.mergedRight = rightShelf;
    }
  };

  unmergeRight = () => {
    if (this.mergedRight) {
      this.mergedRight.mergedLeft = null;
      this.mergedRight = null;
    }
  };

  getMergedWidth = () => {
    const mergedShelves = this.getMergedShelves();
    return mergedShelves.reduce((totalWidth, shelf) => totalWidth + shelf.width, 0);
  };

  /**
   * The items physically standing above THIS board, judged by rendered
   * geometry — the single canonical board assignment for any per-board
   * aggregate (load, counts). Do NOT use `this.items` for that: on a merged
   * run, getCoordinates() lays items out across the whole run and
   * redistributes slack as even gaps, so an item held in one shelf's `items`
   * array can render above a neighbouring board.
   *
   * Every item in the run is assigned to exactly one board: boards own the
   * half-open x-range [boardLeft, boardRight) of their width within the run,
   * and an item belongs to the board its centre x falls in. Items whose
   * centre strays outside the run entirely — an overfilled run pushes items
   * past its end, and nothing today clamps facings — are counted against the
   * first or last board rather than silently dropped.
   *
   * Pegboard placements have no shelf and are invisible to this method by
   * construction; a caller aggregating per-board figures must decide about
   * them explicitly, not assume they are included.
   */
  getItemsPhysicallyOnBoard = (): PlanogramItem[] => {
    const mergedShelves = this.getMergedShelves();

    // This board's x-range within the run.
    let boardLeft = mergedShelves[0].getXLeft();
    for (const shelf of mergedShelves) {
      if (shelf === this) break;
      boardLeft += shelf.width;
    }
    const boardRight = boardLeft + this.width;

    const isFirstBoard = this === mergedShelves[0];
    const isLastBoard = this === mergedShelves[mergedShelves.length - 1];

    const itemsOnBoard: PlanogramItem[] = [];
    for (const shelf of mergedShelves) {
      for (const item of shelf.items) {
        const { xLeft, xRight } = item.getCoordinates();
        const centreX = (xLeft + xRight) / 2;

        const inRange = centreX >= boardLeft && centreX < boardRight;
        const strayedLeft = isFirstBoard && centreX < boardLeft;
        const strayedRight = isLastBoard && centreX >= boardRight;

        if (inRange || strayedLeft || strayedRight) {
          itemsOnBoard.push(item);
        }
      }
    }

    return itemsOnBoard;
  };

  getMerchandisableWidth = () => getEffectiveMerchandisableWidth(this);

  /**
   * A board that holds no product, such as an empty riser, is no space to mix. One holding items
   * keeps its width, since those items count towards the shares.
   */
  offersSpace = () => this.items.length > 0 || this.getMerchandisableWidth() > 0;

  getMergedMerchandisableWidth = () => getMergedMerchandisableWidth(this.getMergedShelves());

  /** Products, caps and the dividers the shelf puts between and around them. Merged shelves are one. */
  getItemsWidth = () => {
    const mergedShelves = this.getMergedShelves();
    const dividerSlots = getShelfItemDividerSlots(mergedShelves.flatMap((shelf) => shelf.items));
    if (dividerSlots) {
      return dividerSlots.reduce((totalWidth, slot) => totalWidth + slot.width, 0);
    }
    return mergedShelves.reduce(
      (totalWidth, shelf) =>
        totalWidth +
        shelf.items.reduce((shelfWidth, item) => shelfWidth + getShelfItemWidth(item), 0),
      0,
    );
  };

  getOverflowWidth = () => Math.max(0, this.getItemsWidth() - this.getMergedMerchandisableWidth());

  getUnusedWidth = () => {
    return this.getMergedMerchandisableWidth() - this.getItemsWidth();
  };

  /**
   * To get shelf utilization in percentage. Merged shelves are considered as one.
   * @returns Shelf utilization in percentage.
   */
  getUtilizationPercentage = () => {
    const merchandisableWidth = this.getMergedMerchandisableWidth();
    const itemsWidth = this.getItemsWidth();

    if (merchandisableWidth === 0) {
      return itemsWidth === 0 ? 0 : Number.POSITIVE_INFINITY;
    }

    return (itemsWidth * 100) / merchandisableWidth;
  };

  /**
   * Whether this shelf's items, in order, fit the free intervals its strips
   * leave — the physical check the aggregate utilization cannot make, since a
   * product wider than any gap between strips can still be under the total.
   * Merged shelves are considered as one.
   */
  fitsAroundDeadSpans = () => {
    const mergedShelves = this.getMergedShelves();
    return itemsFitAroundDeadSpans({
      ...getShelfItemLayout(mergedShelves.flatMap((shelf) => shelf.items)),
      width: this.getMergedWidth(),
      deadSpans: getMergedDeadSpans(mergedShelves),
    });
  };

  /**
   * Helper function to calculate wether shelf is overflowing.
   * @returns true if shelf utilization is more than 100%, or if the items
   * cannot physically stand in order between the shelf's strips.
   */
  isOverflowing = () => {
    return this.getUtilizationPercentage() > 100 || !this.fitsAroundDeadSpans();
  };

  unrangeItems = () => {
    for (const item of this.items) {
      item.unrange();
    }
  };

  moveShelfToBayNo = (bayNo: number) => {
    if (bayNo > 0 && bayNo <= this.bay.planogram.bays.length) {
      // Remove shelf from current bay.
      const index = this.bay.shelves.findIndex((shelf) => shelf.uniqueId === this.uniqueId);
      this.bay.shelves.splice(index, 1);

      // Add shelf to new bay.
      const newBay = this.bay.planogram.bays[bayNo - 1];
      newBay.shelves.push(this);
      this.bay = newBay;

      newBay.fixShelvesOrder();
    }
  };

  setNotchNo = (notchNo: number) => {
    for (const shelf of this.getMergedShelves()) {
      shelf.notchNo = Math.max(1, Math.min(this.bay.maxNotch, notchNo));
      shelf.bay.fixShelvesOrder();
    }
  };

  delete = () => {
    this.unrangeItems();
    this.bay.shelves = this.bay.shelves.filter((shelf) => shelf.uniqueId !== this.uniqueId);

    if (this.mergedLeft) {
      this.unmergeLeft();
    }

    if (this.mergedRight) {
      this.unmergeRight();
    }
  };

  addItem = (item: PlanogramItem | UnrangedPlanogramItem) => {
    this.addItems([item]);
  };

  addItems = (items: (PlanogramItem | UnrangedPlanogramItem)[]) => {
    for (const item of items) {
      let finalItem: PlanogramItem;

      // Planogram item
      if ("shelf" in item) {
        item.shelf.items = item.shelf.items.filter(
          (shelfItem) => shelfItem.uniqueId !== item.uniqueId,
        );
        finalItem = item;
        item.shelf = this;

        // Unranged item
      } else {
        this.bay.planogram.pog.unrangedItems = this.bay.planogram.pog.unrangedItems.filter(
          (unrangedItem) => unrangedItem.uniqueId !== item.uniqueId,
        );
        finalItem = new PlanogramItem(item, this);
      }

      this.items.push(finalItem);
    }
  };

  sortItemsBy = (sortBy: keyof PlanogramItem) => {
    this.items = this.items.sort((item1, item2) => {
      if (item1[sortBy] === null || item1[sortBy] === undefined) return 0;
      if (item2[sortBy] === null || item2[sortBy] === undefined) return 0;
      if (item1[sortBy]! < item2[sortBy]!) return -1;
      if (item1[sortBy]! > item2[sortBy]!) return 1;
      return 0;
    });
  };
}
